<template>
    <LazyHydration :hydrate="hydrateOnIdle()">
        <HeadMetaTwoPage
            title="Best Web Games"
            description="Browse free online games."
        />
    </LazyHydration>
    <div :class="$style.page">
        <div :class="$style.content">
            <header :class="$style.header">
                <Link
                    :to="{ name: ROUTE_NAMES.WHITE_LABEL_2PAGE, query: route.query }"
                    data-interface-target="logo-link"
                    :class="$style.logo_link"
                >
                    <TwoPageBrand />
                </Link>
                <nav :class="$style.category_nav">
                    <Link
                        v-for="section in sections"
                        :key="section.mainCategory"
                        :to="{ ...buildTwoPageCategoryRoute(section.mainCategory), query: route.query }"
                        color="white-60"
                        :class="$style.category_link"
                    >
                        {{ section.title }}
                    </Link>
                </nav>
                <label :class="$style.search">
                    <span :class="$style.search_label">Search games</span>
                    <Icon
                        name="search"
                        :class="$style.search_icon"
                    />
                    <input
                        id="two-page-search"
                        v-model="searchQuery"
                        :class="$style.search_input"
                        type="search"
                        placeholder="Search games"
                        autocomplete="off"
                        inputmode="search"
                    />
                </label>
            </header>
            <main :class="$style.main">
                <section
                    v-for="section in visibleSections"
                    :key="section.mainCategory"
                    :class="$style.section"
                >
                    <ShelfTitle
                        :title="section.title"
                        :categoryId="section.categoryId"
                        :link="section.href ? { ...section.href, query: route.query } : undefined"
                    />
                    <div :class="$style.grid">
                        <GameCard
                            v-for="game in section.games"
                            :key="game.hru"
                            :game="game"
                            :to="buildTwoPageLaunchRoute(game.hru, route.query)"
                            type="rectangle"
                            appearance="medium_horizontal"
                            responsive
                        />
                    </div>
                </section>
                <p
                    v-if="isSearching && visibleSections.length === 0"
                    :class="$style.empty"
                >
                    No games found
                </p>
            </main>
            <TwoPageFooter />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, hydrateOnIdle, onMounted, onServerPrefetch, ref } from 'vue'
import { useRoute } from 'vue-router'

import GameCard from '@/components/GameCard/GameCard.vue'
import ShelfTitle from '@/components/GamesShelf/ShelfTitle.vue'
import Icon from '@/components/Icon/Icon.vue'
import LazyHydration from '@/components/LazyHydration.vue'
import Link from '@/components/Link.vue'
import { ROUTE_NAMES } from '@/router/constants'

import HeadMetaTwoPage from './HeadMetaTwoPage.vue'
import { buildTwoPageCategoryRoute, buildTwoPageLaunchRoute } from './two-page-data'
import TwoPageBrand from './TwoPageBrand.vue'
import TwoPageFooter from './TwoPageFooter.vue'
import { type TwoPageCatalogGame, useTwoPageCatalogData } from './use-two-page-catalog-data'

const route = useRoute()
const { allSections, sections, preloadCatalogData } = useTwoPageCatalogData()
const searchQuery = ref('')
const searchTerms = computed(() => normalizeSearchText(searchQuery.value).split(/\s+/).filter(Boolean))
const isSearching = computed(() => searchTerms.value.length > 0)
const visibleSections = computed(() => {
    if (!isSearching.value) {
        return sections.value
    }

    return allSections.value
        .map((section) => ({
            ...section,
            href: undefined,
            // Search the whole category, not only the capped landing-page preview.
            games: section.allGames.filter(isGameSearchMatch),
        }))
        .filter((section) => section.games.length > 0)
})

function isGameSearchMatch(game: TwoPageCatalogGame) {
    const searchableText = normalizeSearchText(
        [game.title, game.hru, game.slug, game.categoryTitle, game.mainCategory].join(' '),
    )

    return searchTerms.value.every((term) => searchableText.includes(term))
}

function normalizeSearchText(value: string) {
    return value.trim().toLowerCase()
}

onServerPrefetch(async () => {
    await preloadCatalogData()
})

onMounted(async () => {
    await preloadCatalogData()
})
</script>

<style module>
.page {
    min-height: 100svh;
    background:
        radial-gradient(circle at top, rgba(87, 132, 255, 0.18), transparent 34%),
        linear-gradient(180deg, #090d17 0%, #05070d 100%);
    padding: var(--pg-safe-area-top) var(--pg-safe-area-right) var(--pg-safe-area-bottom) var(--pg-safe-area-left);
}

.content {
    width: min(1200px, 100%);
    margin: 0 auto;
    padding-top: 8px;
}

.main {
    padding: 0 8px;
}

.header {
    padding: 0 8px 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.logo_link {
    box-sizing: border-box;
    width: fit-content;
    min-height: 48px;
    border-radius: 24px;
    background: var(--purple-400);
    display: inline-flex;
    align-items: center;
    padding: 0 clamp(12px, 3vw, 18px);
    box-shadow: inset 0 0 0 1px var(--white-25);
}

.category_nav {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
}

.category_link {
    padding: 6px 14px;
    border-radius: 20px;
    border: 1px solid var(--white-25);
    white-space: nowrap;
    font-size: 14px;
}

.search {
    box-sizing: border-box;
    width: 100%;
    max-width: 420px;
    min-height: 56px;
    border-radius: 18px;
    background: var(--carbon-400);
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 20px;
    color: var(--white);
    outline: 1px solid transparent;
}

.search:focus-within {
    background: transparent;
    outline-color: var(--purple-300);
}

.search_label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    white-space: nowrap;
    clip-path: inset(50%);
}

.search_icon {
    width: 28px;
    height: 28px;
    flex: 0 0 auto;
    color: var(--white-80);
}

.search_input {
    min-width: 0;
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--white);
    caret-color: var(--purple-400);
    font-size: var(--font-size-paragraph-l);
    line-height: var(--font-size-paragraph-l);
    padding: 0;
}

.search_input::placeholder {
    color: var(--white-60);
}

.search_input::-webkit-search-cancel-button {
    -webkit-appearance: none;
}

.main {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 8px;
    --cards-gap: 8;
}

.empty {
    color: var(--white-60);
    font-size: var(--font-size-paragraph-m);
    line-height: var(--line-height-paragraph);
    margin: 8px 0 0;
}

@media (--mobile-landscape) {
    .grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (--tablet-plus) {
    .content {
        padding-inline: 0;
    }

    .header {
        padding-bottom: 18px;
        align-items: center;
        justify-content: space-between;
    }

    .logo_link {
        min-height: 56px;
        padding-inline: 20px;
        border-radius: 62px;
    }

    .grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 16px 12px;
        --cards-gap: 12;
    }
}

@media (--desktop-small-plus) {
    .header {
        flex-direction: row;
    }
}
</style>
